import type { Plugin, Component } from 'vue';
import type { Router } from 'vue-router';
import { ClientConstants } from '@/constants/ClientConstants';
import { standardClientProfile, type ClientProfile } from '@/clientConfig/profile';
import type { ClientLogoSet } from '@/utils/clientLogos/types';

/**
 * We dynamically import the router because Vite automatically statically imports all the dependent files if
 * the router is imported statically.
 */
export function getRouterForEnv(): Promise<Router> {
  if (import.meta.env.VITE_CLIENT === ClientConstants.BOFA) {
    return import('@/router/whitelabel/bofa/Router').then((module) => module.default);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.HSBC) {
    return import('@/router/whitelabel/hsbc/Router').then((module) => module.default);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.BARCLAYS) {
    return import('@/router/whitelabel/barclays/Router').then((module) => module.default);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.RBC) {
    return import('@/router/whitelabel/rbc/Router').then((module) => module.default);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.SOCGEN) {
    return import('@/router/whitelabel/socgen/Router').then((module) => module.default);
  }

  return import('@/router/Router').then((module) => module.default);
}

export function getPluginsForEnv(): Promise<Plugin[]> {
  if (import.meta.env.VITE_CLIENT === ClientConstants.BOFA) {
    // dynamic import here as well since this plugin references the BofA router
    return import('./bofaMercury').then((module) => [module.bofaMercuryPlugin]);
  }

  return Promise.resolve([]);
}

/**
 * Hooks that should run after `app.mount` (no-ops when not applicable).
 * RBC: user_active keep-alive. Parent iframe `mounted` is sent later from RBCAuthRoot
 * once the session check leaves PENDING and the router is ready.
 */
export async function notifyAppMountedForEnv(): Promise<void> {
  if (import.meta.env.VITE_CLIENT !== ClientConstants.RBC) return;

  const iframe = await import('@/utils/parentIframeMessaging');
  iframe.startParentIframeUserActiveKeepAlive();
}

/**
 * Components that are used in the app level and are dynamically imported based on the client.
 * If needed, we can add a component for client.
 */
export function getLoginBarForEnv(): Promise<Component> {
  if (import.meta.env.VITE_CLIENT === ClientConstants.HSBC) {
    return import('@/whitelabel/hsbc/layout/components/ApplicationNavbar.vue').then((module) => module.default);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.RBC) {
    return import('@/whitelabel/rbc/layout/components/ApplicationNavbar.vue').then((module) => module.default);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.SOCGEN) {
    return import('@/whitelabel/socgen/layout/components/ApplicationNavbar.vue').then((module) => module.default);
  }

  return import('@/layout/components/navbar/ApplicationNavbar.vue').then((module) => module.default);
}

export function getNavbarForEnv(): Promise<Component> {
  if (import.meta.env.VITE_CLIENT === ClientConstants.HSBC) {
    return import('@/whitelabel/hsbc/layout/components/ApplicationNavbarShell.vue').then((module) => module.default);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.RBC) {
    return import('@/whitelabel/rbc/layout/components/ApplicationNavbarShell.vue').then((module) => module.default);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.SOCGEN) {
    return import('@/whitelabel/socgen/layout/components/ApplicationNavbarShell.vue').then((module) => module.default);
  }

  return import('@/layout/components/navbar/ApplicationNavbarShell.vue').then((module) => module.default);
}

export function getWhitelabelPdfFooterForEnv(): Promise<Component> {
  if (import.meta.env.VITE_CLIENT === ClientConstants.HSBC) {
    return import('@/whitelabel/hsbc/layout/components/pdf/WhitelabelPdfFooter.vue').then((module) => module.default);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.BARCLAYS) {
    return import('@/whitelabel/barclays/layout/components/pdf/WhitelabelPdfFooter.vue').then(
      (module) => module.default,
    );
  }

  return import('@/layout/components/pdf/WhitelabelPdfFooter.vue').then((module) => module.default);
}

/**
 * Client logo assets are dynamically imported so other clients' SVGs are not loaded.
 */
export function getClientLogosForEnv(): Promise<ClientLogoSet> {
  if (import.meta.env.VITE_CLIENT === ClientConstants.BOFA) {
    return import('@/whitelabel/bofa/clientLogos').then((module) => module.clientLogos);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.BARCLAYS) {
    return import('@/whitelabel/barclays/clientLogos').then((module) => module.clientLogos);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.HSBC) {
    return import('@/whitelabel/hsbc/clientLogos').then((module) => module.clientLogos);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.RBC) {
    return import('@/whitelabel/rbc/clientLogos').then((module) => module.clientLogos);
  }

  if (import.meta.env.VITE_CLIENT === ClientConstants.SOCGEN) {
    return import('@/whitelabel/socgen/clientLogos').then((module) => module.clientLogos);
  }

  return import('@/utils/clientLogos/Premialab').then((module) => module.clientLogos);
}

/**
 * Per-client UI and routing metadata for this build. Whitelabels with dedicated
 * profiles are code-split; others use the standard Premialab-shaped defaults.
 */
export function getClientConfigForEnv(): Promise<ClientProfile> {
  if (import.meta.env.VITE_CLIENT === ClientConstants.HSBC) {
    return import('@/whitelabel/hsbc/clientConfig').then((module) => module.clientConfig);
  }

  return Promise.resolve(standardClientProfile(import.meta.env.VITE_CLIENT as ClientConstants));
}
